import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { DollarSign, Plus, Trash2, Loader2, Wrench, Package, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { workOrderCostsApi as costsApi } from '../services/api';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';

/**
 * Costs panel for a Work Order.
 *
 * Shows the labour roll-up (auto-calculated on completion from time tracker
 * × user.hourly_rate) plus a Parts/Misc line-item ledger that admins +
 * mechanics can edit until the WO is completed. Once completed, the card
 * goes read-only.
 *
 * Backend contract:
 *   GET    /api/work-orders/{id}/costs           → summary + line items
 *   POST   /api/work-orders/{id}/costs           → add line item (parts|misc)
 *   DELETE /api/work-orders/{id}/costs/{itemId}  → remove line item
 *
 * Note: Labour cost is computed at completion time on the server. This card
 * just surfaces the value; there's no editing of labour here.
 */
const formatMoney = (cents) => {
  const n = Number(cents) || 0;
  return n.toLocaleString(undefined, { style: 'currency', currency: 'AUD' });
};

const formatHours = (totalSeconds) => {
  const s = Number(totalSeconds) || 0;
  if (s === 0) return '0h';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const blankDraft = { description: '', category: 'parts', quantity: '1', unit_cost: '0', supplier: '', part_number: '' };

const WorkOrderCostsCard = ({ workOrderId, isLocked, canEdit, onCostsChanged }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(blankDraft);
  const [adding, setAdding] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // {id, description}
  const [deleting, setDeleting] = useState(false);
  const [showAddRow, setShowAddRow] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await costsApi.getSummary(workOrderId);
      setSummary(res.data);
    } catch (e) {
      // Silent failure — empty state handles it.
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (workOrderId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId]);

  const handleAdd = async () => {
    const description = (draft.description || '').trim();
    const quantity = parseFloat(draft.quantity);
    const unit_cost = parseFloat(draft.unit_cost);
    if (!description) {
      toast.error('Description is required');
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      toast.error('Quantity must be a positive number');
      return;
    }
    if (!Number.isFinite(unit_cost) || unit_cost < 0) {
      toast.error('Unit cost must be 0 or greater');
      return;
    }
    setAdding(true);
    try {
      await costsApi.addItem(workOrderId, {
        description,
        category: draft.category,
        quantity,
        unit_cost,
        supplier: draft.supplier?.trim() || undefined,
        part_number: draft.part_number?.trim() || undefined,
      });
      toast.success('Line item added');
      setDraft(blankDraft);
      setShowAddRow(false);
      await load();
      if (onCostsChanged) onCostsChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add line item');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await costsApi.deleteItem(workOrderId, pendingDelete.id);
      toast.success('Line item removed');
      setPendingDelete(null);
      await load();
      if (onCostsChanged) onCostsChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove line item');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 flex justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const items = summary?.line_items || [];
  const partsItems = items.filter((i) => i.category === 'parts');
  const miscItems = items.filter((i) => i.category === 'misc');
  const editingAllowed = canEdit && !isLocked;

  return (
    <>
      <Card data-testid="wo-costs-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-sm uppercase tracking-wide flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-[#FFD108]" />
              Costs
            </CardTitle>
            <span className="font-mono text-sm">
              <span className="text-muted-foreground">Total · </span>
              <span className="text-foreground font-semibold" data-testid="wo-costs-total">
                {formatMoney(summary?.total_cost)}
              </span>
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Roll-up grid */}
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="rounded-sm border border-border bg-secondary/30 px-3 py-2">
              <div className="flex items-center gap-1.5 text-muted-foreground uppercase tracking-widest text-[10px] mb-1">
                <Clock className="w-3 h-3" />
                Labour
              </div>
              <div className="font-mono font-semibold" data-testid="wo-labour-cost">{formatMoney(summary?.labour_cost)}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">{formatHours(summary?.total_time_seconds)}</div>
            </div>
            <div className="rounded-sm border border-border bg-secondary/30 px-3 py-2">
              <div className="flex items-center gap-1.5 text-muted-foreground uppercase tracking-widest text-[10px] mb-1">
                <Package className="w-3 h-3" />
                Parts
              </div>
              <div className="font-mono font-semibold" data-testid="wo-parts-cost">{formatMoney(summary?.parts_cost)}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">{partsItems.length} item{partsItems.length === 1 ? '' : 's'}</div>
            </div>
            <div className="rounded-sm border border-border bg-secondary/30 px-3 py-2">
              <div className="flex items-center gap-1.5 text-muted-foreground uppercase tracking-widest text-[10px] mb-1">
                <Wrench className="w-3 h-3" />
                Misc
              </div>
              <div className="font-mono font-semibold" data-testid="wo-misc-cost">{formatMoney(summary?.misc_cost)}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">{miscItems.length} item{miscItems.length === 1 ? '' : 's'}</div>
            </div>
          </div>

          {/* Line-items table */}
          {items.length > 0 ? (
            <div className="rounded-sm border border-border overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/30">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium">Item</th>
                    <th className="px-3 py-2 font-medium w-16">Type</th>
                    <th className="px-3 py-2 font-medium text-right w-12">Qty</th>
                    <th className="px-3 py-2 font-medium text-right w-24">Unit</th>
                    <th className="px-3 py-2 font-medium text-right w-24">Total</th>
                    {editingAllowed && <th className="px-2 py-2 w-8"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border font-mono">
                  {items.map((it) => (
                    <tr key={it.id} data-testid={`wo-cost-row-${it.id}`}>
                      <td className="px-3 py-2">
                        <div className="text-foreground">{it.description}</div>
                        {(it.supplier || it.part_number) && (
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            {it.supplier ? `${it.supplier}` : ''}
                            {it.supplier && it.part_number ? ' · ' : ''}
                            {it.part_number ? `PN ${it.part_number}` : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-[10px] uppercase">{it.category}</Badge>
                      </td>
                      <td className="px-3 py-2 text-right">{it.quantity}</td>
                      <td className="px-3 py-2 text-right">{formatMoney(it.unit_cost)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-foreground">{formatMoney(it.total)}</td>
                      {editingAllowed && (
                        <td className="px-2 py-2 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => setPendingDelete({ id: it.id, description: it.description })}
                            data-testid={`wo-cost-delete-${it.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-3 border border-dashed border-border rounded-sm">
              No parts or miscellaneous costs recorded yet.
            </p>
          )}

          {/* Add new line */}
          {editingAllowed && !showAddRow && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddRow(true)}
              className="w-full"
              data-testid="wo-cost-add-toggle"
            >
              <Plus className="w-3.5 h-3.5 mr-2" />
              Add parts or misc cost
            </Button>
          )}

          {editingAllowed && showAddRow && (
            <div className="rounded-sm border border-primary/30 bg-primary/[0.03] p-3 space-y-3" data-testid="wo-cost-add-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Description</Label>
                  <Input
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    placeholder="e.g. Hydraulic filter element"
                    data-testid="wo-cost-description"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Type</Label>
                  <Select
                    value={draft.category}
                    onValueChange={(v) => setDraft({ ...draft, category: v })}
                  >
                    <SelectTrigger data-testid="wo-cost-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parts">Parts</SelectItem>
                      <SelectItem value="misc">Misc</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Qty</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={draft.quantity}
                    onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
                    data-testid="wo-cost-quantity"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Unit cost</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={draft.unit_cost}
                    onChange={(e) => setDraft({ ...draft, unit_cost: e.target.value })}
                    data-testid="wo-cost-unit-cost"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Supplier (opt)</Label>
                  <Input
                    value={draft.supplier}
                    onChange={(e) => setDraft({ ...draft, supplier: e.target.value })}
                    placeholder="—"
                    data-testid="wo-cost-supplier"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Part # (opt)</Label>
                  <Input
                    value={draft.part_number}
                    onChange={(e) => setDraft({ ...draft, part_number: e.target.value })}
                    placeholder="—"
                    data-testid="wo-cost-part-number"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <span className="text-xs text-muted-foreground mr-auto font-mono">
                  Subtotal · <span className="text-foreground font-semibold">
                    {formatMoney((parseFloat(draft.quantity) || 0) * (parseFloat(draft.unit_cost) || 0))}
                  </span>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setDraft(blankDraft); setShowAddRow(false); }}
                  disabled={adding}
                  data-testid="wo-cost-cancel"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleAdd}
                  disabled={adding}
                  className="btn-volt"
                  data-testid="wo-cost-save"
                >
                  {adding ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-2" />}
                  Add
                </Button>
              </div>
            </div>
          )}

          {isLocked && (
            <p className="text-[11px] text-muted-foreground border-l-2 border-border pl-2 mt-1">
              Work order completed — costs are now read-only.
            </p>
          )}
        </CardContent>
      </Card>

      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
        title="Remove cost line?"
        description={pendingDelete ? `"${pendingDelete.description}" will be removed and totals recalculated.` : ''}
        loading={deleting}
        onConfirm={handleDelete}
        testId="confirm-delete-cost-line"
        confirmLabel="Remove"
      />
    </>
  );
};

export default WorkOrderCostsCard;
